import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

/**
 * AntiCapture — best-effort screenshot / screen-recording deterrents.
 *
 * IMPORTANT REALITY CHECK
 * -----------------------
 * The browser cannot actually prevent OS-level screenshots or screen recording.
 * The Print Screen key, macOS Cmd+Shift+3/4/5, Snipping Tool, phone cameras,
 * capture cards, and screen recorders all sit below the browser and are
 * uninterceptable. Anything we do here is a *deterrent* that raises the effort
 * for a casual leaker — combined with the diagonal watermark, any leaked
 * screenshot is traceable back to a specific user and session.
 *
 * What this component does:
 *   1. Blocks right-click context menu (kiosk-style).
 *   2. Blocks Ctrl/Cmd+P (browser print) and Ctrl/Cmd+S (save page).
 *   3. Swallows PrintScreen key (won't stop the OS capture, but does stop any
 *      script-triggered screenshot flow).
 *   4. Adds CSS `@media print { body { display: none } }` to blank the page
 *      if someone manages to invoke the browser print dialog anyway.
 *   5. Blurs the entire app when the tab loses focus (best-effort mitigation
 *      against background screen recorders that might capture other tabs).
 *   6. Overlays a diagonal watermark with the current user's email + session
 *      timestamp so any screenshot is provably attributable.
 *   7. Logs `screenshot_attempt` audit events any time PrintScreen or the
 *      OS screenshot key combos are pressed.
 */

const AUDIT_SESSION_ID_KEY = "virtus_audit_session";

function getSessionId() {
    let sid = sessionStorage.getItem(AUDIT_SESSION_ID_KEY);
    if (!sid) {
        sid =
            (typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : Math.random().toString(36).slice(2)) +
            "-" +
            Date.now();
        sessionStorage.setItem(AUDIT_SESSION_ID_KEY, sid);
    }
    return sid;
}

/** Fire-and-forget audit log — batched via requestAnimationFrame so quick
 * bursts collapse to a single request. */
const _pending = [];
let _flushHandle = null;
function pushAudit(event) {
    _pending.push(event);
    if (_flushHandle) return;
    _flushHandle = setTimeout(async () => {
        const batch = _pending.splice(0, _pending.length);
        _flushHandle = null;
        try {
            await api.post("/audit/log", { session_id: getSessionId(), events: batch });
        } catch (_e) {
            // Non-fatal — losing an audit line is preferable to blocking UX.
        }
    }, 400);
}

export default function AntiCapture() {
    const { user } = useAuth();
    const location = useLocation();
    const [blurred, setBlurred] = useState(false);
    const lastLoggedPath = useRef({ path: "", ts: 0 });

    // ---- Deterrents (mount once) -------------------------------------------
    useEffect(() => {
        const onContextMenu = (e) => {
            e.preventDefault();
        };
        const onKeyDown = (e) => {
            // Ctrl/Cmd + P → block browser print entirely
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
                e.preventDefault();
                e.stopPropagation();
                pushAudit({ action: "print_blocked", path: location.pathname });
            }
            // Ctrl/Cmd + S → block save-page
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
                e.preventDefault();
                e.stopPropagation();
                pushAudit({ action: "save_blocked", path: location.pathname });
            }
            // PrintScreen key — swallow it (OS may still capture)
            if (e.key === "PrintScreen") {
                e.preventDefault();
                pushAudit({ action: "screenshot_attempt", path: location.pathname, meta: { key: "PrintScreen" } });
            }
            // Best-guess macOS screenshot combos (Cmd+Shift+3/4/5)
            if (
                e.metaKey &&
                e.shiftKey &&
                ["3", "4", "5"].includes(e.key)
            ) {
                pushAudit({
                    action: "screenshot_attempt",
                    path: location.pathname,
                    meta: { key: `Cmd+Shift+${e.key}` },
                });
            }
        };

        document.addEventListener("contextmenu", onContextMenu);
        window.addEventListener("keydown", onKeyDown, true);

        // Inject a print-blocking style once so the browser's own print
        // dialog (which we can't fully suppress) yields a blank page.
        const styleId = "virtus-anticapture-print";
        if (!document.getElementById(styleId)) {
            const s = document.createElement("style");
            s.id = styleId;
            // textContent (not innerHTML) — this string never contains markup
            // but using textContent removes the entire XSS attack surface.
            s.textContent = `@media print { body { display: none !important; } }`;
            document.head.appendChild(s);
        }

        return () => {
            document.removeEventListener("contextmenu", onContextMenu);
            window.removeEventListener("keydown", onKeyDown, true);
        };
    }, [location.pathname]);

    // ---- Blur on tab focus loss --------------------------------------------
    useEffect(() => {
        const onVisibility = () => {
            const hidden = document.hidden;
            setBlurred(hidden);
            if (hidden) {
                pushAudit({ action: "tab_hidden", path: location.pathname });
            } else {
                pushAudit({ action: "tab_visible", path: location.pathname });
            }
        };
        const onBlur = () => {
            setBlurred(true);
            pushAudit({ action: "window_blur", path: location.pathname });
        };
        const onFocus = () => setBlurred(false);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("blur", onBlur);
        window.addEventListener("focus", onFocus);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("blur", onBlur);
            window.removeEventListener("focus", onFocus);
        };
    }, [location.pathname]);

    // ---- Page-view audit (throttled to 1/30s per path) ---------------------
    useEffect(() => {
        const now = Date.now();
        if (
            location.pathname === lastLoggedPath.current.path &&
            now - lastLoggedPath.current.ts < 30000
        ) {
            return;
        }
        lastLoggedPath.current = { path: location.pathname, ts: now };
        pushAudit({ action: "page_view", path: location.pathname });
    }, [location.pathname]);

    // Apply the blur to the root element so overlays + watermark share it.
    useEffect(() => {
        const root = document.getElementById("root");
        if (!root) return;
        if (blurred) root.style.filter = "blur(28px) saturate(0.7)";
        else root.style.filter = "";
        return () => {
            if (root) root.style.filter = "";
        };
    }, [blurred]);

    if (!user) return null;

    // Watermark is intentionally scoped to the Production Documents area
    // *below* the platforms landing page — i.e. subsystems, folder browser,
    // and any deeper file view. The rest of the app (Hub, Dev Plan, Fleet,
    // etc.) is designed for the public 60" TV and doesn't need the overlay.
    // Deterrents (right-click block, print block, blur-on-focus-loss,
    // screenshot-attempt logging) stay active globally.
    const showWatermark =
        location.pathname.startsWith("/production/docs/") &&
        location.pathname !== "/production/docs";
    if (!showWatermark) return null;

    // ---- Watermark overlay -------------------------------------------------
    // Non-interactive, tiled diagonally, low-contrast so it doesn't spoil the
    // kiosk look but IS present in every screenshot.
    const stamp = new Date().toISOString().replace("T", " ").slice(0, 19) + "Z";
    const line = `${user.email} · ${stamp}`;
    return (
        <div
            aria-hidden
            className="pointer-events-none fixed inset-0 z-[200] select-none overflow-hidden"
            style={{
                background: `repeating-linear-gradient(-30deg,
                    transparent 0 220px,
                    rgba(255, 85, 0, 0.045) 220px 340px)`,
                mixBlendMode: "screen",
            }}
        >
            <div className="absolute inset-0 flex flex-wrap items-start justify-start gap-x-40 gap-y-16 px-8 pt-12 -rotate-[18deg] origin-top-left translate-y-16">
                {Array.from({ length: 50 }).map((_, i) => (
                    <span
                        key={`wm-tile-${i}`}
                        className="font-mono text-[11px] tracking-[0.28em] uppercase whitespace-nowrap"
                        style={{ color: "rgba(255, 85, 0, 0.055)" }}
                    >
                        VIRTUS AI · {line}
                    </span>
                ))}
            </div>
        </div>
    );
}

/** Helper for other components to push their own audit events. */
export function auditEvent(action, path, meta) {
    pushAudit({ action, path: path || window.location.pathname, meta: meta || null });
}
